<template>
  <Teleport to="body">
    <div v-if="visible" class="country-modal-overlay" @click.self="handleOverlayClick">
      <div class="country-modal">
        <div class="country-modal__header">
          <h2 class="country-modal__title">{{ t('profile.countrySelection.title') }}</h2>
          <p class="country-modal__subtitle">
            {{ t('profile.countrySelection.subtitle') }}
          </p>
        </div>

        <div class="country-modal__search">
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="country-modal__search-input"
            :placeholder="t('profile.countrySelection.searchPlaceholder')"
            autocomplete="off"
          />
        </div>

        <div class="country-modal__list">
          <div
            v-for="country in filteredCountries.slice(0, visibleCount)"
            :key="country.code"
            :class="[
              'country-item',
              { 'country-item--selected': selectedCode === country.code },
              { 'country-item--loading': savingCode === country.code }
            ]"
            @click="selectCountry(country)"
          >
            <img
              :src="getFlagUrl(country.code)"
              :alt="t(`countries.${country.code}`)"
              class="country-item__flag"
              loading="lazy"
              @error="onFlagError"
            />
            <span class="country-item__name">{{ t(`countries.${country.code}`) }}</span>
            <span v-if="savingCode === country.code" class="country-item__spinner"/>
            <svg
              v-else-if="selectedCode === country.code"
              class="country-item__check"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path d="M5 13l4 4L19 7"
stroke="currentColor"
stroke-width="2"
stroke-linecap="round"
stroke-linejoin="round"/>
            </svg>
          </div>

          <div
            v-if="filteredCountries.length > visibleCount"
            ref="loadMoreTrigger"
            class="country-modal__load-more"
          >
            {{ t('common.loading') }}
          </div>

          <div v-if="filteredCountries.length === 0" class="country-modal__empty">
            {{ t('profile.countrySelection.notFound') }}
          </div>
        </div>

        <div class="country-modal__footer">
          <!-- Terms Checkbox - only show after country is selected -->
          <label v-if="selectedCode" class="country-modal__terms">
            <input
              v-model="termsAccepted"
              type="checkbox"
              class="country-modal__terms-checkbox"
            />
            <span class="country-modal__terms-text">
              {{ t('profile.countrySelection.termsAgree') }}
              <a :href="termsUrl" target="_blank" class="country-modal__terms-link">{{ t('profile.countrySelection.termsLink') }}</a>
            </span>
          </label>

          <button
            class="country-modal__submit"
            :disabled="!selectedCode || !termsAccepted"
            @click="close"
          >
            {{ t('profile.countrySelection.continue') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { COUNTRIES, type CountryOption } from '../data/countries'
import { useProfileStore } from '../store/profileStore'

const { t } = useI18n()

const props = defineProps<{
  visible: boolean
  required?: boolean
}>()

const emit = defineEmits<{
  (e: 'select', payload: { code: string; name: string }): void
  (e: 'close'): void
}>()

const profileStore = useProfileStore()

const searchQuery = ref('')
const selectedCode = ref<string | null>(null)
const selectedName = ref<string | null>(null)
const savingCode = ref<string | null>(null)
const termsAccepted = ref(false)
const visibleCount = ref(20)
const searchInput = ref<HTMLInputElement | null>(null)
const loadMoreTrigger = ref<HTMLElement | null>(null)

// Debounced search
const debouncedQuery = ref('')
let searchTimeout: ReturnType<typeof setTimeout> | null = null

watch(searchQuery, (val) => {
  if (searchTimeout) clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    debouncedQuery.value = val
    visibleCount.value = 20
  }, 200)
})

const filteredCountries = computed(() => {
  const query = debouncedQuery.value.toLowerCase().trim()
  if (!query) return COUNTRIES
  
  return COUNTRIES.filter(c =>
    t(`countries.${c.code}`).toLowerCase().includes(query) ||
    c.nameEn.toLowerCase().includes(query) ||
    c.code.toLowerCase() === query
  )
})

// Terms URL - will support country-specific URLs in the future
const termsUrl = computed(() => {
  // TODO: Return different URLs based on selectedCode
  // Example: if (selectedCode.value === 'RU') return '/terms/ru'
  return '/app/law/terms'
})

function getFlagUrl(code: string): string {
  return `/assets/css/flags/flags/1x1/${code.toLowerCase()}.svg`
}

function onFlagError(e: Event) {
  const img = e.target as HTMLImageElement
  img.style.visibility = 'hidden'
}

async function selectCountry(country: CountryOption) {
  // Don't reselect if already selected or saving
  if (selectedCode.value === country.code || savingCode.value) return
  
  savingCode.value = country.code
  
  try {
    await profileStore.updateCountry(country.code)
    selectedCode.value = country.code
    selectedName.value = t(`countries.${country.code}`)
  } catch (error) {
    console.error('Failed to save country:', error)
  } finally {
    savingCode.value = null
  }
}

function handleOverlayClick() {
  if (!props.required) {
    emit('close')
  }
}

function close() {
  if (selectedCode.value && selectedName.value && termsAccepted.value) {
    emit('select', { code: selectedCode.value, name: selectedName.value })
    emit('close')
  }
}

// Infinite scroll
let observer: IntersectionObserver | null = null

function setupInfiniteScroll() {
  if (!loadMoreTrigger.value) return
  
  observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && visibleCount.value < filteredCountries.value.length) {
      visibleCount.value = Math.min(visibleCount.value + 20, filteredCountries.value.length)
    }
  }, { rootMargin: '100px' })
  
  observer.observe(loadMoreTrigger.value)
}

watch(() => props.visible, (visible) => {
  if (visible) {
    nextTick(() => {
      searchInput.value?.focus()
      setupInfiniteScroll()
    })
  } else {
    observer?.disconnect()
  }
})

onMounted(() => {
  if (props.visible) {
    setupInfiniteScroll()
  }
})

onUnmounted(() => {
  observer?.disconnect()
  if (searchTimeout) clearTimeout(searchTimeout)
})
</script>

<style scoped>
.country-modal-overlay {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 16px;
}

.country-modal {
  background: var(--hm-surface, var(--ds-surface));
  border-radius: var(--ds-radius-modal);
  width: 100%;
  max-width: 440px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 40px var(--ds-scrim);
  animation: modalSlideIn 0.2s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.country-modal__header {
  padding: 24px 24px 16px;
  text-align: center;
}

.country-modal__title {
  font-size: var(--ds-text-size-h-sm);
  font-weight: var(--ds-weight-bold);
  color: var(--hm-text-primary);
  margin: 0 0 8px;
}

.country-modal__subtitle {
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-secondary);
  margin: 0;
  line-height: 1.5;
}

.country-modal__search {
  padding: 0 24px 16px;
}

.country-modal__search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--hm-border);
  border-radius: var(--ds-radius-card);
  background: var(--hm-surface, var(--ds-surface));
  color: var(--hm-text-primary);
  font-size: var(--ds-text-size-body);
  outline: none;
  transition: border-color 0.2s;
}

.country-modal__search-input:focus {
  border-color: var(--hm-accent);
}

.country-modal__search-input::placeholder {
  color: var(--hm-text-secondary);
}

.country-modal__list {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px;
  max-height: 280px;
}

.country-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: var(--ds-radius-card);
  cursor: pointer;
  transition: background-color 0.15s;
}

.country-item:hover {
  background: var(--hm-surface-alt, var(--ds-surface-canvas));
}

.country-item--selected {
  background: var(--ds-accent-soft-08);
}

.country-item--loading {
  opacity: 0.7;
  pointer-events: none;
}

.country-item__flag {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid var(--hm-border);
}

.country-item__name {
  flex: 1;
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-primary);
}

.country-item__check {
  width: 20px;
  height: 20px;
  color: var(--hm-accent);
  flex-shrink: 0;
}

.country-item__spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--hm-border);
  border-top-color: var(--hm-accent);
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  flex-shrink: 0;
}

.country-modal__load-more,
.country-modal__empty {
  padding: 24px;
  text-align: center;
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-secondary);
}

.country-modal__footer {
  padding: 16px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.country-modal__terms {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
}

.country-modal__terms-checkbox {
  width: 18px;
  height: 18px;
  margin-top: 2px;
  accent-color: var(--hm-accent);
  cursor: pointer;
}

.country-modal__terms-text {
  font-size: var(--p-text-13);
  line-height: 1.4;
  color: var(--hm-text-secondary);
}

.country-modal__terms-link {
  color: var(--hm-accent);
  text-decoration: none;
}

.country-modal__terms-link:hover {
  text-decoration: underline;
}

.country-modal__submit {
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: var(--p-radius-50);
  background: var(--hm-accent);
  color: var(--ds-surface);
  font-size: var(--p-text-15);
  font-weight: var(--ds-weight-semibold);
  cursor: pointer;
  transition: opacity 0.2s, transform 0.1s;
}

.country-modal__submit:hover:not(:disabled) {
  opacity: 0.9;
}

.country-modal__submit:active:not(:disabled) {
  transform: scale(0.98);
}

.country-modal__submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Mobile responsiveness */
@media (max-width: 480px) {
  .country-modal-overlay {
    padding: 0;
    align-items: flex-end;
  }
  
  .country-modal {
    max-width: 100%;
    max-height: 90vh;
    border-radius: var(--ds-radius-modal) var(--ds-radius-modal) 0 0;
  }
  
  .country-modal__list {
    max-height: 45vh;
  }
}
</style>
